import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ArrowLeft, Check, Info } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/shadcn/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/shadcn/tabs";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { NoxusInput } from "@/components/Form/InputField";
import { MAX_ENTITY_NAME } from "@/constants/entityLimits";
import { ConfirmationDialog } from "@/components/Dialogs/ConfirmationDialog";
import {
  AgentTestsT,
  CHECK_GROUPS,
  CHECK_KINDS,
  CheckDraft,
  CheckKind,
  Expected,
  checkDescription,
  checkLabel,
  checkName,
  defaultExpected,
  generatedName,
  groupLabel,
  isComplete,
  kindOf,
  modeLabel,
  needsNoValue,
  switchMode,
} from "./checkCatalog";
import { CheckContext, CheckFields } from "./CheckFields";

type CheckValues = { type: string; expected: Expected; name: string };

function CheckPicker({
  t,
  onPick,
}: {
  t: AgentTestsT;
  onPick: (kind: CheckKind) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      {CHECK_GROUPS.map((group) => (
        <div key={group}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {groupLabel(t, group)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {CHECK_KINDS.filter((kind) => kind.group === group).map((kind) => {
              const Icon = kind.icon;
              return (
                <Button
                  key={kind.key}
                  data-testid="check-kind"
                  type="button"
                  variant="outline"
                  onClick={() => onPick(kind)}
                  className="h-auto items-start justify-start gap-3 whitespace-normal rounded-lg p-3 text-left font-normal leading-normal"
                >
                  <div className="flex items-center justify-center size-8 rounded-md bg-muted shrink-0 mt-0.5">
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex flex-col gap-1">
                    <p className="text-sm font-medium leading-tight">
                      {checkName(t, kind)}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {checkDescription(t, kind)}
                    </p>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CheckDrawer({
  initial,
  context,
  onSave,
  onClose,
}: {
  initial: CheckDraft | null;
  context: CheckContext;
  onSave: (check: CheckDraft) => Promise<void> | void;
  onClose: () => void;
}) {
  const t = useTranslations("agentTests");
  const [kind, setKind] = useState<CheckKind | null>(
    initial ? (kindOf(initial.type) ?? null) : null
  );
  const form = useForm<CheckValues>({
    defaultValues: {
      type: initial?.type ?? "",
      expected: initial?.expected ?? {},
      name: initial?.name ?? "",
    },
  });
  const { type, expected, name } = useWatch({ control: form.control });
  const [isNameWritten, setIsNameWritten] = useState(
    initial !== null &&
      !!initial.name &&
      initial.name !== checkLabel(t, initial.type) &&
      initial.name !== generatedName(t, initial.type, initial.expected)
  );
  const shownName = isNameWritten
    ? (name ?? "")
    : generatedName(t, type ?? "", expected ?? {});
  // Only what the user typed counts: browsing the mode tabs is not a change.
  const [isEdited, setIsEdited] = useState(false);
  // "back" returns to the catalogue on discard; true closes the drawer.
  const [isDiscarding, setIsDiscarding] = useState<boolean | "back">(false);
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = initial !== null;
  const requestClose = () => (isEdited ? setIsDiscarding(true) : onClose());
  const modes = kind?.modes ?? [];
  const valuesByMode = useRef<Record<string, Expected>>({});

  const chooseMode = (next: string) => {
    const current = form.getValues();
    valuesByMode.current[current.type] = current.expected;
    form.setValue(
      "expected",
      valuesByMode.current[next] ?? switchMode(next, current.expected)
    );
    form.setValue("type", next);
  };

  const pick = (picked: CheckKind) => {
    const [first] = picked.modes;
    setKind(picked);
    valuesByMode.current = {};
    setIsNameWritten(false);
    form.reset({
      type: first.type,
      expected: defaultExpected(first.type),
      name: "",
    });
  };

  const save = form.handleSubmit(async (values) => {
    setIsSaving(true);
    try {
      await onSave({
        evaluatorId: initial?.evaluatorId,
        type: values.type,
        expected: values.expected,
        name: isNameWritten
          ? values.name.trim()
          : generatedName(t, values.type, values.expected),
      });
      onClose();
    } catch {
      // The save already showed why it failed; the form stays open to retry.
      setIsSaving(false);
    }
  });

  return (
    <Sheet open onOpenChange={(open) => !open && requestClose()}>
      <SheetContent
        className="w-full gap-0 shadow-xl sm:max-w-xl"
        onEscapeKeyDown={(event) => {
          if (isEdited) {
            event.preventDefault();
            // A prompt mounted while Escape is dispatched is dismissed by it.
            requestAnimationFrame(() => setIsDiscarding(true));
          }
        }}
        data-testid="check-drawer"
      >
        <SheetHeader className="flex flex-row items-start border-0 mx-6 pr-8">
          <div className="flex items-start gap-2 min-w-0">
            {kind && !isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("drawer.back")}
                onClick={() =>
                  isEdited ? setIsDiscarding("back") : setKind(null)
                }
                className="size-6 mt-0.5"
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <div className="flex flex-col gap-1 min-w-0">
              <SheetTitle>
                {kind
                  ? checkName(t, kind)
                  : isEditing
                    ? t("drawer.editTitle")
                    : t("drawer.addTitle")}
              </SheetTitle>
              <SheetDescription>
                {kind ? checkDescription(t, kind) : t("drawer.description")}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 pb-10">
          {!kind ? (
            <CheckPicker t={t} onPick={pick} />
          ) : (
            <div className="flex flex-col gap-4">
              {modes.length > 1 && (
                <Tabs value={type} onValueChange={chooseMode}>
                  <TabsList className="w-auto">
                    {modes.map((mode) => (
                      <TabsTrigger key={mode.type} value={mode.type}>
                        {modeLabel(t, mode.type)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}
              {needsNoValue(type ?? "") ? (
                <NoxusAlert
                  variant="info"
                  icon={Info}
                  title={t("drawer.noValueTitle")}
                  description={t("drawer.noValueDescription")}
                />
              ) : (
                <Controller
                  control={form.control}
                  name="expected"
                  render={({ field }) => (
                    <CheckFields
                      key={type}
                      type={type ?? ""}
                      expected={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setIsEdited(true);
                      }}
                      context={context}
                    />
                  )}
                />
              )}
              <NoxusInput
                id="check-name"
                name="check-name"
                label={t("drawer.name")}
                helperText={t("drawer.nameHelp")}
                // A name saved through the API can be longer; keep it editable.
                maxLength={Math.max(
                  MAX_ENTITY_NAME,
                  initial?.name?.length ?? 0
                )}
                value={shownName}
                onChange={(event) => {
                  const next = event.target.value;
                  setIsNameWritten(next.trim() !== "");
                  form.setValue("name", next);
                  setIsEdited(true);
                }}
              />
            </div>
          )}
        </div>

        {kind && (
          <SheetFooter className="border-t px-6">
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={requestClose}>
                {t("cancel")}
              </Button>
              <Button
                type="button"
                disabled={!isComplete(type ?? "", expected ?? {}) || isSaving}
                loading={isSaving}
                onClick={save}
              >
                <Check className="size-4" />
                {isEditing ? t("drawer.saveCheck") : t("drawer.addCheck")}
              </Button>
            </div>
          </SheetFooter>
        )}
        <ConfirmationDialog
          title={t("drawer.discardTitle")}
          description={t("drawer.discardDescription")}
          confirmText={t("drawer.discard")}
          cancelText={t("drawer.keepEditing")}
          confirmVariant="destructive"
          isOpen={isDiscarding !== false}
          onOpenChange={(open) => setIsDiscarding(open)}
          onConfirm={() => {
            if (isDiscarding === "back") {
              setIsDiscarding(false);
              setIsEdited(false);
              setKind(null);
              return;
            }
            setIsDiscarding(false);
            onClose();
          }}
        />
      </SheetContent>
    </Sheet>
  );
}
